/**
 * Declarative route-data table.
 *
 * **Data-only, no JSX.** Exports a typed `RouteDescriptor[]` and nothing
 * else. `<Routes>` / `<Route>` JSX lives exclusively in
 * `./RouteRegistry.tsx`, which is the single production-source file
 * whitelisted by T044's `no-flat-route-jsx` architecture check.
 *
 * **Shape — discriminated union on `kind`.** A descriptor is either a
 * `'render'` descriptor (mounts a component, optionally guarded) or a
 * `'redirect'` descriptor (emits `<Navigate>` at its path). The
 * discriminator is what the type system uses to make these mutually
 * exclusive: a `'render'` descriptor cannot carry a `redirect` field
 * and a `'redirect'` descriptor cannot carry a `component`. A
 * descriptor with `{ kind: 'render', redirect: '/foo' }` or
 * `{ kind: 'redirect', component: Profile }` fails to type-check.
 *
 *   - `path` — the URL pattern React Router matches against. Required
 *     on every descriptor.
 *   - `kind` — `'render'` | `'redirect'`. The discriminator.
 *
 *   For `'render'` descriptors:
 *     - `component`: rendered at that path via `<Component />`. Eager
 *       `ComponentType` or a lazy admin component (FR-018 code-split,
 *       see `./lazyAdminRoutes.ts`).
 *     - `protected` — whether `RouteRegistry` wraps the component in
 *       `RouteGuard`. `false` only for `/login` (the member entry point);
 *       `true` for every other render descriptor (member-gated content,
 *       profile, messages, contact, my-cases, the
 *       `/forbidden` bounce target, the `/admin` launch portal, and the
 *       admin sections at `/admin/feedback` and `/admin/cms/*`). The admin
 *       surfaces add a `role: 'admin'` narrowing on top, so the guard
 *       handles every non-admin audience there (loading → placeholder,
 *       anonymous → server `/admin/login` with `returnTo`, member →
 *       `/forbidden`).
 *     - `role` — optional. When set, `RouteGuard` additionally gates on
 *       session role. The `Role` type re-exported from `SessionPort` so
 *       descriptors and the guard share one role alphabet.
 *     - `portBoundary` — optional. When set, `RouteRegistry` wraps the
 *       rendered component in `<PortErrorBoundary portName={...}>` with
 *       a `<ContentUnavailable />` fallback so a port throw at runtime
 *       lands on a Finnish-first surface rather than React's default
 *       error UI. The string value becomes the boundary's `portName`
 *       prop (e.g. `'content'`). See `RouteRegistry.tsx` for the wrap
 *       order (`Chrome → RouteGuard → PortErrorBoundary → Component`).
 *     - `chrome` — optional. `'portal-home'` mounts the bandless
 *       `<AdminPortalHomeLayout>` (the `/admin` springboard); `'portal'`
 *       mounts the slim `<AdminPortalLayout>` (portal-styled sections);
 *       `'cms'` mounts the slim `<AdminCmsLayout>`; `'bare'` mounts NO chrome
 *       wrapper — a standalone full-viewport surface (e.g. the auth terminal
 *       screens at `/ei-jasenyytta` and `/tunnistus-epaonnistui`) that renders
 *       no member shell behind it (D-130); omitted (the default) mounts the
 *       member `<Layout>`. See `RouteRegistry.tsx` §"Chrome dispatch".
 *
 *   For `'redirect'` descriptors:
 *     - `redirect` — the target path. `RouteRegistry` renders
 *       `<Navigate to={redirect} replace />`. The descriptor stays in
 *       the table so the URL is a valid registered route (no
 *       react-router miss); the redirect is the descriptor's only
 *       behaviour. The destination route owns its own guarding —
 *       redirect descriptors do not carry a `protected` flag because
 *       there is no rendered component to guard. Used by
 *       `/account → /profile` per D-048 (FR-030 amended) so historical
 *       bookmarks land at the new surface without surfacing the legacy
 *       URL in user-facing UI.
 *
 * Phase A PR B / PR C.3 extended this table with the generic content,
 * bespoke proto-aligned, and slug-pinned re-export routes plus the
 * `/jasenedut` stub (`phase-a-content-inventory.md` §"Frontend route
 * inventory"): descriptors only, no JSX edits in `RouteRegistry.tsx`.
 * Future clusters extend the same way (push entries here).
 *
 * Thirteen content-port-backed render descriptors carry
 * `portBoundary: 'content'` so a `ContentReaderPort` throw lands on
 * `<ContentUnavailable />` instead of React's default error UI (TA22
 * port-isolation closure): the three generic and five bespoke
 * proto-aligned and three slug-pinned routes (which consume `useArticle`
 * or `useCategories`), plus the 029 `/lakiruutu`
 * (`useVideoListings`, US2) and `/jasenedut` (`useBenefits`, US3) CMS
 * surfaces. Home (`/`) is deliberately omitted: `HomeCards`'s curated
 * strip already carries its own inline error UX (`home.cards.errorState`),
 * and a route-level boundary would replace that graceful-degradation
 * surface with a full takeover, contradicting FR-054's "Home stays
 * reachable when content fails" posture.
 */

import type { AnalyticsPageKey } from '@omaplus/shared-types';
import type { ComponentType, LazyExoticComponent } from 'react';
import type { Capability } from '../membership/capabilities.js';
import { Home } from '../routes/Home.js';
import { Login } from '../routes/Login.js';
import { MessageDetail } from '../routes/MessageDetail.js';
import { Messages } from '../routes/Messages.js';
import { NotFound } from '../routes/NotFound.js';
import { PaymentReturn } from '../routes/PaymentReturn.js';
import { Profile } from '../routes/Profile.js';
import type { Role } from '../session/SessionPort.js';
// Admin-area descriptors (the `/forbidden` bounce, the `/admin` portal,
// `/admin/feedback`, and `/admin/cms/*`) live in a sibling data-only
// fragment so this table stays under the 400-line `file-size-400` ceiling
// (FR-045). They are spread into `ROUTES` below. The lazy admin component
// references they hold stay in `./lazyAdminRoutes.ts` (the FR-018 seam).
import { ADMIN_ROUTES } from './adminRoutes.js';
// Terminal auth screens (032 US2): standalone `chrome: 'bare'` surfaces the
// member callback 303s the browser to on a confirmed non-membership
// (`/ei-jasenyytta`) or an unreachable/error verify outcome
// (`/tunnistus-epaonnistui`). PUBLIC so `RouteGuard` does not wall the
// anonymous (cookieless) terminal visitor.
import { CouldNotVerify } from './CouldNotVerify.js';
import { CONTENT_ROUTES } from './contentRoutes.js';
import { NoMembershipBlock } from './NoMembershipBlock.js';

export type RouteRole = Role;

/**
 * A descriptor that mounts a component at a path, optionally wrapped
 * in `RouteGuard` for session/role gating.
 */
export interface RouteRenderDescriptor {
  readonly path: string;
  readonly kind: 'render';
  // Eager component or a lazy admin component (see file header + `./lazyAdminRoutes.ts`).
  readonly component: ComponentType | LazyExoticComponent<ComponentType>;
  readonly protected: boolean;
  readonly role?: RouteRole;
  /**
   * Opt the route into a `<PortErrorBoundary>` wrap at registry-render
   * time. When set, `RouteRegistry` projects the element as
   * `<PortErrorBoundary portName={portBoundary} fallback={<ContentUnavailable />}>
   * <Component /></PortErrorBoundary>`, so any throw inside the route
   * (typically a port-backed query rejecting and the consumer
   * re-throwing the error to the boundary) lands on the Finnish-first
   * fallback rather than React's default error UI.
   *
   * The string value becomes the boundary's `portName` prop and is the
   * developer-facing display hint surfaced in the diagnostic region.
   *
   * Typed as a literal-union (rather than `string`) so the discriminator
   * matches the strictness of the sibling fields (`kind`, `role`) and
   * typos like `'cotnent'` fail to compile rather than silently
   * no-opping at runtime. The union is intentionally one-element today —
   * `'content'` is the only port whose boundary is mounted by the
   * registry. Future ports (e.g. `'lakikaveri'`, `'settings'`) widen
   * this union when they actually land at registry-render time.
   *
   * Wrap order is `Chrome → RouteGuard → PortErrorBoundary → Component`
   * (see `RouteRegistry.tsx`): chrome is outermost (so a `<Navigate>`
   * bounce keeps the shell painted), then the guard, then the
   * boundary, then the component.
   */
  readonly portBoundary?: 'content';
  /**
   * Selects the shell chrome wrapper. `'portal-home'` mounts the bandless
   * `<AdminPortalHomeLayout>` (the `/admin` launch-portal springboard — a
   * clean centered screen with no top band, matching the proto); `'portal'`
   * mounts the slim `<AdminPortalLayout>` (the "Ylläpito" band shared by the
   * portal-styled sections, e.g. `/admin/feedback`); `'cms'` mounts the slim
   * `<AdminCmsLayout>` (logo + page-title only, no member chrome). `'bare'`
   * mounts NO chrome wrapper at all — a standalone full-viewport surface (the
   * auth terminal screens at `/ei-jasenyytta` and `/tunnistus-epaonnistui`,
   * 032 US2) that renders no member shell behind it, satisfying D-130's
   * "the member shell is NOT rendered behind the hard-block" via the route
   * itself rather than a Layout early-return. Omitted is the implicit default:
   * the member `<Layout>` (SkipLink + TopBar + main + PrimaryNav +
   * MembershipExpiredBanner + MenuSheet). 025-admin-area added the
   * `'portal'` + `'portal-home'` variants alongside 021-cms's `'cms'`; the
   * launch portal home (`/admin`) selects `'portal-home'`, its portal-styled
   * sections (e.g. `/admin/feedback`) select `'portal'`; 032 US2 added `'bare'`
   * for the terminal auth routes. See `RouteRegistry.tsx` for the dispatch and
   * wrap-order reasoning.
   *
   * Shape mirrors `portBoundary` (`'content' | undefined`) — both are
   * opt-in projections, not structural discriminators, so `undefined`
   * is the implicit default. An explicit `'member' | 'cms' | 'portal' |
   * 'portal-home' | 'bare'` would force every member descriptor to grow a
   * redundant `chrome: 'member'` without changing behaviour.
   */
  readonly chrome?: 'cms' | 'portal' | 'portal-home' | 'bare';
  /**
   * Membership-capability override (042-membership-access-binary, D-202,
   * superseding 028). `RouteRegistry` default-denies every `role: 'member'`
   * route to `'other_actions'`; a route may set a lower-bar capability here.
   * Under binary access a live member session only ever carries `'valid'` (which
   * allows every capability), so this override is now only load-bearing for the
   * fail-closed `'none'` case — there is no lapsed carve-out any more. See
   * `RouteRegistry.tsx` and `RouteGuard`.
   */
  readonly requiredCapability?: Capability;
  /** Safe fixed key emitted only after this admitted route commits. */
  readonly analyticsPageKey?: AnalyticsPageKey;
}

/**
 * A descriptor that redirects from `path` to `redirect` via
 * `<Navigate replace>`. Carries no `component` or `protected` — the
 * destination route owns its own guarding.
 */
export interface RouteRedirectDescriptor {
  readonly path: string;
  readonly kind: 'redirect';
  readonly redirect: string;
}

export type RouteDescriptor = RouteRenderDescriptor | RouteRedirectDescriptor;

export const ROUTES: readonly RouteDescriptor[] = [
  { path: '/login', kind: 'render', component: Login, protected: false },
  // Terminal auth screens (032 US2). PUBLIC (`protected: false`) so `RouteGuard`
  // never walls the anonymous, cookieless visitor the member callback 303s here;
  // `chrome: 'bare'` so they render standalone with NO member shell behind
  // (D-130). `/ei-jasenyytta` = confirmed non-membership; `/tunnistus-epaonnistui`
  // = unreachable/error verify outcome.
  {
    path: '/ei-jasenyytta',
    kind: 'render',
    component: NoMembershipBlock,
    protected: false,
    chrome: 'bare',
  },
  {
    path: '/tunnistus-epaonnistui',
    kind: 'render',
    component: CouldNotVerify,
    protected: false,
    chrome: 'bare',
  },
  {
    path: '/',
    kind: 'render',
    component: Home,
    protected: true,
    analyticsPageKey: { kind: 'memberRoute', route: 'home' },
  },
  // `/messages` + `/profile` set an explicit `requiredCapability` (042/D-202: a
  // valid member allows every capability; the override only gates the `none` case).
  {
    path: '/messages',
    kind: 'render',
    component: Messages,
    protected: true,
    role: 'member',
    requiredCapability: 'open_messages',
    analyticsPageKey: { kind: 'memberRoute', route: 'messages' },
  },
  // Unified Viestit detail (030 US2/US3, FR-006): the canonical `/messages/:id`
  // (plural, matching the list) replaces the demo singular `/message/:id` and
  // absorbs the retired `/my-cases/:id`. Same guard + `requiredCapability` as
  // /messages (042/D-202 binary access).
  {
    path: '/messages/:id',
    kind: 'render',
    component: MessageDetail,
    protected: true,
    role: 'member',
    requiredCapability: 'open_messages',
  },
  {
    path: '/profile',
    kind: 'render',
    component: Profile,
    protected: true,
    role: 'member',
    requiredCapability: 'view_latest_invoice',
  },
  // Paytrail payment-return view (044 US1 / FR-002a). Matches the api redirect
  // base (`${PAYTRAIL_REDIRECT_BASE_URL}/maksu/palaa`): the member's browser
  // lands here on return from the Paytrail hosted page. A member route (the
  // returning browser still carries the member cookie — same-origin); the view
  // is display-only and reads `checkout-status` to show pending vs cancel/fail.
  {
    path: '/maksu/palaa',
    kind: 'render',
    component: PaymentReturn,
    protected: true,
    role: 'member',
  },
  // ----- Content surfaces (Phase A + bespoke + re-exports + 029 CMS) -----
  //
  // The generic /content templates, the bespoke proto-aligned pages, the
  // slug-pinned re-exports, and the 029 CMS-driven listings live in the
  // sibling `./contentRoutes.ts` data-only fragment (spread here) so this
  // table stays under the 400-line `file-size-400` ceiling (FR-045) — the
  // same extraction pattern as `./adminRoutes.ts`. Their routing rationale
  // is documented at each descriptor in that fragment. Push new content
  // descriptors there, not here.
  ...CONTENT_ROUTES,
  // ----- 030 US3: the standalone "Omat asiat" surface (`/my-cases` +
  // `/my-cases/:id`) is RETIRED and folded into the unified "Viestit" (D-220).
  // The real member-cases read/detail it consumed now backs `/messages` (the
  // single Viestit list — D-220(i): no Saapuneet/Lähetetyt tabs) and
  // `/messages/:id` (detail, above). No `/my-cases` redirect
  // is kept (owner decision 2026-09-16): unlike `/account → /profile`, the
  // surface had no nav entry and there is no production audience holding those
  // URLs yet — dropping them is intentional, not an oversight. -----
  // `/account` → `/profile` redirect per D-048 (FR-030 amended). The path
  // stays in the registry so bookmarks resolve; no UI references `/account`.
  { path: '/account', kind: 'redirect', redirect: '/profile' },

  // ----- Admin-area surfaces (025-admin-area + 021-cms) -----
  //
  // The `/forbidden` bounce target, the `/admin` launch portal,
  // `/admin/feedback`, and the `/admin/cms/*` authoring shell live in the
  // sibling `./adminRoutes.ts` data-only fragment (spread here) so this
  // table stays under the 400-line `file-size-400` ceiling (FR-045). Their
  // dense routing rationale (loop-safe `role: 'admin'` gating, portal vs CMS
  // chrome, the anonymous-on-admin server-sign-in redirect, the eager
  // `/forbidden` member-bundle posture) is documented at each descriptor in
  // that fragment. Push new admin descriptors there, not here.
  ...ADMIN_ROUTES,

  // ----- The catch-all, last by reading order (react-router ranks a splat
  // below every other pattern, so its position here is for the reader, not
  // for matching) -----
  //
  // The app's answer for an address that does not exist. The edge serves the
  // SPA shell for ANY unknown path, so the client router already received
  // control and matched nothing; without this descriptor `RouteRegistry`
  // rendered no route at all and the behaviour was undefined rather than
  // chosen (`RouteRegistry.tsx` says a catch-all belongs here). One decision
  // for every unmatched address — a MOVED address is a different question and
  // keeps its own redirect descriptor (`/account → /profile`).
  //
  // `protected: true` with NO `role`, the `/forbidden` shape: an unknown
  // address then behaves exactly like every other address in this app —
  // anonymous visitors reach `/login` rather than the member shell, and no
  // membership capability gates a page that offers nothing to gate (a
  // `role: 'member'` descriptor would default to the `other_actions`
  // capability, which would grey a not-found page for a `none` member).
  { path: '*', kind: 'render', component: NotFound, protected: true },
];
